import { Files, Plus } from "@phosphor-icons/react";
import { Link, useSearch } from "@tanstack/react-router";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { GitConnection } from "~/api/gitTypes";
import type { Project } from "~/api/governanceTypes";
import type { Collection } from "~/api/types";
import { RepositoryNodes } from "~/features/git/RepoTree";
import { cn } from "~/foundation/utils/cn";
import {
  CollectionNode,
  sortCollections,
  TREE_ROW_ACTIVE_CLASS,
  TREE_ROW_CLASS,
  TreeCaret,
} from "~/shell/Sidebar/CollectionTree";
import { useShellStore } from "~/shell/store";

/**
 * The sidebar's project tree (§A.3.2, refined 2026-10-11): every project, its name opening the
 * project's work board, and — only when the project actually holds something — a disclosure
 * for its collections (each browsable like the Collections section) and repositories. A project
 * with nothing linked is a plain link: no caret, no "none yet" row.
 *
 * The data is fetched once by the sidebar and handed in, so N projects cost no extra requests.
 */
export function ProjectTree({
  projects,
  collections,
  connections,
  pathname,
  onNavigate,
}: {
  projects: Project[];
  collections: Collection[];
  connections: GitConnection[];
  pathname: string;
  onNavigate?: (() => void) | undefined;
}) {
  const { t } = useTranslation();

  if (!projects.length) {
    return (
      <button
        type="button"
        onClick={() => useShellStore.getState().openDialog({ kind: "newProject" })}
        className={cn(TREE_ROW_CLASS, "text-text-tertiary hover:text-text")}
      >
        <Plus size={16} aria-hidden className="shrink-0" />
        <span className="truncate">{t("New project")}</span>
      </button>
    );
  }

  return (
    <ul className="flex flex-col gap-0.5">
      {projects.map((project) => (
        <ProjectNode
          key={project.id}
          project={project}
          collections={sortCollections(collections.filter((c) => c.projectId === project.id))}
          connections={connections.filter((c) => c.projectId === project.id)}
          pathname={pathname}
          onNavigate={onNavigate}
        />
      ))}
    </ul>
  );
}

function ProjectNode({
  project,
  collections,
  connections,
  pathname,
  onNavigate,
}: {
  project: Project;
  collections: Collection[];
  connections: GitConnection[];
  pathname: string;
  onNavigate?: (() => void) | undefined;
}) {
  const isExpanded = useShellStore((state) => state.expandedProjects[project.id] === true);
  const toggleExpanded = useShellStore((state) => state.toggleProjectExpanded);
  const search = useSearch({ strict: false }) as { project?: string };
  const contentsId = useId();
  const hasContents = collections.length > 0 || connections.length > 0;
  const isActive = pathname === "/work" && search.project === project.id;

  return (
    <li>
      <div className="flex items-center gap-0.5">
        <TreeCaret
          expanded={hasContents && isExpanded}
          controls={contentsId}
          name={project.name}
          onToggle={hasContents ? () => toggleExpanded(project.id) : null}
        />
        <Link
          to="/work"
          search={{ project: project.id } as never}
          onClick={onNavigate}
          aria-current={isActive ? "page" : undefined}
          title={project.description || project.name}
          className={cn(TREE_ROW_CLASS, isActive && TREE_ROW_ACTIVE_CLASS)}
        >
          {project.icon ? (
            <span aria-hidden className="w-4 shrink-0 text-center text-small leading-none">
              {project.icon}
            </span>
          ) : (
            <Files size={16} aria-hidden className="shrink-0 text-text-tertiary" />
          )}
          <span className="truncate">{project.name}</span>
        </Link>
      </div>
      {hasContents && isExpanded ? (
        <div id={contentsId} className="flex flex-col gap-0.5 ps-3">
          {collections.length ? (
            <ul className="flex flex-col gap-0.5">
              {collections.map((collection) => (
                <CollectionNode
                  key={collection.id}
                  collection={collection}
                  pathname={pathname}
                  onNavigate={onNavigate}
                />
              ))}
            </ul>
          ) : null}
          <RepositoryNodes connections={connections} pathname={pathname} onNavigate={onNavigate} />
        </div>
      ) : null}
    </li>
  );
}
